'use client';
import { forwardRef, RefObject, useEffect, useId, useState, useImperativeHandle, useRef } from 'react';
import cn from 'classnames';
import styles from './modal.module.scss';
import { CloseIcon } from '@/assets/icons/CloseIcon';
import { createPortal } from 'react-dom';
import { type ButtonProps, Button } from '@/ui-components/atoms/Button/Button';
import { Text } from '@/ui-components/atoms/Text/Text';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

const MODAL_INERT_EXCEPTION_SELECTOR = '#onetrust-consent-sdk, #onetrust-banner-sdk, #onetrust-pc-sdk';

export interface ModalProps {
    children?: React.ReactNode;
    open: boolean;
    onClose?: () => void;
    className?: string;
    pauseFocus?: boolean;
    maxWidth?: number | 'none';
    backgroundTransparent?: boolean;
    isOverflowHidden?: boolean;
    variant?: 'medium' | 'small' | 'video' | 'plancard';
    headline?: React.ReactNode;
    subhead?: React.ReactNode;
    paragraph?: React.ReactNode;
    ctas?: ButtonProps[];
    containerRef?: React.RefObject<HTMLElement | null>;
    dataLocalpagename?: string;
    contentClassName?: string;
    centeredLayout?: boolean;
    keepContentsMounted?: boolean;
    ariaLabel?: string;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    initialFocusRef?: RefObject<HTMLElement | null>;
    restoreFocusRef?: RefObject<HTMLElement | null>;
}

export const Modal = forwardRef<HTMLDivElement, ModalProps>(
    (
        {
            open,
            onClose,
            children,
            className,
            maxWidth,
            backgroundTransparent,
            isOverflowHidden,
            variant,
            headline,
            subhead,
            paragraph,
            ctas,
            containerRef,
            dataLocalpagename,
            contentClassName,
            centeredLayout,
            keepContentsMounted,
            ariaLabel,
            ariaLabelledBy,
            ariaDescribedBy,
            initialFocusRef,
            restoreFocusRef,
        },
        ref,
    ) => {
        const [isClient, setIsClient] = useState(false);
        const descId = `desc-${useId()}`;
        const headlineId = `headline-${useId()}`;
        const localRef = useRef<HTMLDivElement>(null);
        const dialogRef = useRef<HTMLDialogElement>(null);
        const closeButtonRef = useRef<HTMLButtonElement>(null);

        useImperativeHandle(ref, () => localRef.current as HTMLDivElement);

        useEffect(() => {
            setIsClient(true);
        }, []);

        useBodyScrollLock(open && variant !== 'plancard');

        useEffect(() => {
            const handleKeyUp = (e: KeyboardEvent) => {
                if (e.key === 'Escape') onClose?.();
            };

            if (open) {
                document.addEventListener('keyup', handleKeyUp);
            }

            return () => {
                document.removeEventListener('keyup', handleKeyUp);
            };
        }, [open, onClose]);

        useEffect(() => {
            if (!open || !localRef.current || keepContentsMounted) {
                return;
            }

            const modalElement = localRef.current;
            const consentRoots = Array.from(document.querySelectorAll(MODAL_INERT_EXCEPTION_SELECTOR));
            const siblings = Array.from(document.body.children).filter(
                (element) => !element.contains(modalElement) && !consentRoots.includes(element),
            );

            const cleanupEntries = siblings.map((element) => {
                const htmlElement = element as HTMLElement;

                return {
                    element: htmlElement,
                    ariaHidden: htmlElement.getAttribute('aria-hidden'),
                    inert: htmlElement.inert,
                };
            });

            cleanupEntries.forEach(({ element }) => {
                element.setAttribute('aria-hidden', 'true');
                element.inert = true;
            });

            return () => {
                cleanupEntries.forEach(({ element, ariaHidden, inert }) => {
                    if (ariaHidden === null) {
                        element.removeAttribute('aria-hidden');
                    } else {
                        element.setAttribute('aria-hidden', ariaHidden);
                    }

                    element.inert = inert;
                });
            };
        }, [open, keepContentsMounted, isClient]);

        useEffect(() => {
            if (!dataLocalpagename || !localRef.current) {
                return;
            }

            if (open) {
                localRef.current.dispatchEvent(
                    new CustomEvent('modalopen', { bubbles: true, detail: { pagename: dataLocalpagename } }),
                );
            } else {
                localRef.current.dispatchEvent(new CustomEvent('modalclose', { bubbles: true }));
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [open]);

        useEffect(() => {
            if (!open || !localRef.current) {
                return;
            }

            const modalElement = localRef.current;
            const focusedAtOpen = document.activeElement as HTMLElement | null;
            const restoreTarget =
                restoreFocusRef?.current ||
                (focusedAtOpen && !modalElement.contains(focusedAtOpen) ? focusedAtOpen : null);
            let focusRetryTimer: ReturnType<typeof setTimeout> | undefined;

            const focusFrame = requestAnimationFrame(() => {
                modalElement.scrollTop = 0;

                const focusTarget = initialFocusRef?.current || closeButtonRef.current || dialogRef.current;
                if (!focusTarget) {
                    return;
                }

                focusTarget.focus({ preventScroll: true });

                // iOS VoiceOver can ignore focus on newly-mounted modal content unless focus is retried.
                focusRetryTimer = setTimeout(() => {
                    const activeElement = document.activeElement;
                    if (activeElement !== focusTarget && !modalElement.contains(activeElement)) {
                        focusTarget.focus({ preventScroll: true });
                    }
                }, 350);
            });

            return () => {
                cancelAnimationFrame(focusFrame);

                if (focusRetryTimer) {
                    clearTimeout(focusRetryTimer);
                }

                const activeElement = document.activeElement;
                const focusLeftWithModal =
                    !activeElement || activeElement === document.body || modalElement.contains(activeElement);

                if (focusLeftWithModal && restoreTarget?.isConnected) {
                    restoreTarget.focus({ preventScroll: true });
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [open, isClient]);

        if (!isClient && !keepContentsMounted) return null;
        if (!open && !keepContentsMounted) return null;
        const portalTarget = variant === 'plancard' || keepContentsMounted ? containerRef?.current : document.body;

        const modalClasses = cn(styles.modal, className, {
            [styles.opened]: open,
            [styles[variant ?? 'medium']]: variant,
            [styles.centered]: centeredLayout,
        });

        const containerClasses = cn(styles.container, {
            [styles.transparentBackground]: backgroundTransparent,
            [styles.overflowHidden]: isOverflowHidden,
        });

        return (
            <PortalIfNotKeepMounted
                keepContentsMounted={!!keepContentsMounted}
                portalTarget={portalTarget}
                containerRef={containerRef}
            >
                <div
                    className={modalClasses}
                    ref={localRef}
                    data-componenttype="modal"
                    data-localpagename={dataLocalpagename}
                >
                    <div className={styles.overlay} aria-hidden="true" role="presentation" onClick={onClose} />

                    {onClose && variant === 'video' && (
                        <button
                            ref={closeButtonRef}
                            className={styles.closeX}
                            aria-label="Close modal"
                            onClick={onClose}
                        >
                            <CloseIcon />
                        </button>
                    )}

                    <dialog
                        open={open}
                        ref={dialogRef}
                        className={containerClasses}
                        style={{ maxWidth: maxWidth || '' }}
                        aria-hidden={!open}
                        aria-modal="true"
                        aria-label={ariaLabel}
                        aria-labelledby={ariaLabelledBy || (headline ? headlineId : undefined)}
                        aria-describedby={ariaDescribedBy || descId}
                        tabIndex={-1}
                    >
                        {onClose && variant !== 'video' && (
                            <button
                                ref={closeButtonRef}
                                className={styles.closeX}
                                aria-label="Close modal"
                                onClick={onClose}
                            >
                                <CloseIcon />
                            </button>
                        )}
                        <div className={cn(styles.content, contentClassName)} id={descId}>
                            {(headline || subhead || paragraph) && (
                                <div className={styles.headerGroup}>
                                    {headline && (
                                        <div id={headlineId}>
                                            <Text
                                                tag="h1"
                                                variant={
                                                    variant === 'plancard' ? 'h6' : variant === 'small' ? 'h5' : 'h4'
                                                }
                                                className={styles.headline}
                                            >
                                                {headline}
                                            </Text>
                                        </div>
                                    )}
                                    {subhead && (
                                        <Text tag="div" className={styles.subhead} variant="text-medium">
                                            {subhead}
                                        </Text>
                                    )}
                                    {paragraph && (
                                        <Text tag="div" className={styles.paragraph} variant="text-medium">
                                            {paragraph}
                                        </Text>
                                    )}
                                </div>
                            )}

                            {children}

                            {!!ctas?.length && (
                                <div className={styles.ctaGroup}>
                                    {ctas.map((cta, index) => (
                                        <Button key={index} {...cta} type="button" size="small" />
                                    ))}
                                </div>
                            )}
                        </div>
                    </dialog>
                </div>
            </PortalIfNotKeepMounted>
        );
    },
);
Modal.displayName = 'Modal';

interface PortalIfNotKeepMountedProps {
    children: React.ReactNode;
    keepContentsMounted: boolean;
    portalTarget: HTMLElement | null | undefined;
    containerRef?: React.RefObject<HTMLElement | null>;
}
function PortalIfNotKeepMounted({
    children,
    keepContentsMounted,
    portalTarget,
    containerRef,
}: PortalIfNotKeepMountedProps): React.ReactNode {
    if (keepContentsMounted) {
        return children;
    }

    return portalTarget ? createPortal(children, containerRef?.current || document.body) : null;
}
